import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SEO from "../components/SEO";
import { servicesData, testimonials, aboutInfo } from "../staticData";
import BeforeAfterComparison from "../components/BeforeAfterComparison";
import { 
  Star, 
  Shield, 
  Clock, 
  Award, 
  Phone, 
  ArrowRight,
  CheckCircle
} from "lucide-react";

const Home = () => {
  // Use static data instead of API
  const servicesLoading = false;
  return (
    <div className="min-h-screen">
      <SEO 
        title="WOW AUTOBODY - Professional Auto Body Repair & Collision Services"
        description="Expert auto body repair, collision repair, dent removal, and painting services. Serving the community since 2010 with ASE certified technicians. Free estimates, insurance claims assistance."
        keywords="auto body repair, collision repair, dent removal, car painting, auto body shop, insurance claims, scratch repair, vehicle restoration"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-24 px-4 overflow-hidden">
        {/* Racing stripes background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-2 bg-red-600"></div>
          <div className="absolute top-2 left-0 w-full h-2 bg-white"></div>
          <div className="absolute top-4 left-0 w-full h-2 bg-blue-600"></div>
          <div className="absolute bottom-4 left-0 w-full h-2 bg-blue-600"></div>
          <div className="absolute bottom-2 left-0 w-full h-2 bg-white"></div>
          <div className="absolute bottom-0 left-0 w-full h-2 bg-red-600"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[600px]">
            {/* Left side - Business Title and Info */}
            <div className="text-left">
              <Badge className="bg-yellow-500 text-black mb-6 px-6 py-3 text-lg font-bold">
                🏁 #1 AUTO BODY SHOP IN TOWN
              </Badge>
              
              {/* Logo-inspired title */}
              <div className="mb-8">
                <div className="relative">
                  <h1 className="text-8xl md:text-9xl font-black mb-4 leading-none">
                    <span className="text-yellow-400 text-stroke-black">WOW</span>
                  </h1>
                  <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">
                    AUTO BODY
                  </h2>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-8 h-8 bg-checkered rounded"></div>
                    <div className="flex gap-1">
                      <div className="w-4 h-8 bg-red-600"></div>
                      <div className="w-4 h-8 bg-white"></div>
                      <div className="w-4 h-8 bg-blue-600"></div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xl md:text-2xl mb-8 max-w-2xl leading-relaxed text-gray-200">
                Professional collision repair, dent removal, and auto painting services. 
                We restore your vehicle to its original beauty with precision and care.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-4 text-lg font-bold shadow-xl hover:shadow-2xl transition-all">
                    Get Free Estimate
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/gallery">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-lg font-semibold transition-all">
                    View Our Work
                  </Button>
                </Link>
              </div>

              {/* Racing-themed stats */}
              <div className="grid grid-cols-3 gap-6 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">14+</div>
                  <div className="text-sm text-gray-400">Years Restoring</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-400">15K+</div>
                  <div className="text-sm text-gray-400">Cars Restored</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">98%</div>
                  <div className="text-sm text-gray-400">Success Rate</div>
                </div>
              </div>
            </div>

            {/* Right side - Before/After Drag Comparison */}
            <div className="relative">
              <BeforeAfterComparison />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <Card className="text-center p-6 border-2 hover:border-blue-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-blue-900" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Quality Guarantee</h3>
                <p className="text-gray-600">Lifetime warranty on all our repair work</p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-2 hover:border-red-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fast Service</h3>
                <p className="text-gray-600">Most repairs completed within reasonable window</p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-2 hover:border-blue-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-blue-900" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Experts</h3>
                <p className="text-gray-600">ASE certified technicians with 15+ years experience</p>
              </CardContent>
            </Card>

            <Card className="text-center p-6 border-2 hover:border-red-200 hover:shadow-lg transition-all">
              <CardContent className="p-0">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Insurance Approved</h3>
                <p className="text-gray-600">We work with all major insurance companies</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From minor scratches to major collision damage, we handle it all with precision and care.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {servicesLoading ? (
              // Loading state for services
              Array.from({ length: 4 }).map((_, index) => (
                <Card key={index} className="p-6 border-2">
                  <CardContent className="p-0">
                    <div className="animate-pulse">
                      <div className="bg-gray-300 h-6 rounded mb-3"></div>
                      <div className="bg-gray-200 h-4 rounded mb-4"></div>
                      <div className="bg-gray-200 h-4 rounded mb-2"></div>
                      <div className="bg-gray-200 h-4 rounded"></div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              servicesData?.slice(0, 4).map((service) => (
                <Card key={service.id} className="p-6 hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200">
                  <CardContent className="p-0">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {service.features?.slice(0, 2).map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    {service.price_range && service.price_range !== "Contact for estimate" && (
                      <p className="text-sm text-blue-600 font-semibold mt-3">
                        {service.price_range}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-3 text-lg">
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            {aboutInfo.stats.map((stat, index) => (
              <div key={index} className="p-6">
                <div className="text-4xl md:text-5xl font-bold text-red-400 mb-2">{stat.value}</div>
                <div className="text-lg text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-xl text-gray-600">Don't just take our word for it</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-6 border-2 hover:border-blue-200 hover:shadow-lg transition-all">
                <CardContent className="p-0">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.text}"</p>
                  <p className="font-semibold text-gray-900">- {testimonial.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-6">Ready to Restore Your Vehicle?</h2>
          <p className="text-xl mb-8 opacity-90">
            Get a free estimate today and see why thousands of customers trust WOW AUTOBODY
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-semibold shadow-xl">
                Get Free Estimate
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold">
              <Phone className="mr-2 h-5 w-5" />
              Call Now: (410) 292-5300
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;